import { RouteRecordRaw } from 'vue-router';
import { RouteMetaType, ExtendedRouteMetaType } from '@router/routes/types';
import tenantAdminRoutes from '@router/routes/tenant-admin-app';
import { ENTRY_POINTS } from '@constants/entry-points';

type TenantAppRouteMetaType = RouteMetaType & {
  requiresAuth: true;
  requiresTenant: true;
  requiresTenantAdmin: boolean;
  requiresEnterpriseLicense: boolean;
};

const META_DEFAULT: TenantAppRouteMetaType = Object.freeze({
  requiresAuth: true,
  requiresTenant: true,
  requiresTenantAdmin: false,
  requiresEnterpriseLicense: false,
  redirectOnError: ENTRY_POINTS.PATH.USER_HOME(),
  layout: 'primary',
});

const getMeta = (
  extendedMeta: ExtendedRouteMetaType<TenantAppRouteMetaType> = {},
): TenantAppRouteMetaType => ({ ...META_DEFAULT, ...extendedMeta });

const routes: Array<RouteRecordRaw> = [
  {
    path: '/workspace',
    component: () => import('@views/tenant-app/TenantRootView.vue'),
    redirect: ENTRY_POINTS.PATH.TENANT_HOME(),
    meta: getMeta(),
    children: [
      {
        path: 'dashboard',
        name: ENTRY_POINTS.PATH.TENANT_HOME().name,
        component: () => import('@views/tenant-app/TenantDashboardView.vue'),
        meta: getMeta({ title: 'Dashboard' }),
      },
      {
        path: 'search',
        name: ENTRY_POINTS.PATH.TENANT_APP_SEARCH().name,
        component: () => import('@views/tenant-app/SearchView.vue'),
        meta: getMeta({ title: 'Search' }),
      },
      {
        path: 'self-reviews',
        name: ENTRY_POINTS.PATH.SKILL_TREES().name,
        component: () => import('@views/tenant-app/SkillTreeListView.vue'),
        meta: getMeta({ title: 'Self-Reviews' }),
      },
      {
        path: 'settings',
        name: ENTRY_POINTS.PATH.TENANT_APP_SETTINGS().name,
        component: () => import('@views/tenant-app/SettingsView.vue'),
        meta: getMeta({ title: 'Settings' }),
      },
      {
        path: 'skills/explorer',
        name: 'skills-explorer',
        component: () => import('@views/tenant-app/TenantSkillsGraphView.vue'),
        meta: getMeta({ title: 'Skills Explorer' }),
      },
      {
        path: 'skills-hub',
        component: () => import('@views/tenant-app/SkillsHubRootView.vue'),
        redirect: ENTRY_POINTS.PATH.SKILLS_HUB_HOME(),
        meta: getMeta(),
        children: [
          {
            path: 'dashboard',
            name: ENTRY_POINTS.PATH.SKILLS_HUB_HOME().name,
            component: () => import('@views/tenant-app/SkillsHubDashboardView.vue'),
            meta: getMeta({ title: 'Dashboard | Skills Hub' }),
          },
          {
            path: 'assessments/history/import',
            name: ENTRY_POINTS.PATH.ASSESSMENT_IMPORT_HISTORY().name,
            component: () => import('@views/tenant-app/AssessmentImportHistoryView.vue'),
            meta: getMeta({ title: 'Assessment | Upload History' }),
          },
          {
            path: 'assessments/:assessmentId/skill-scores',
            name: ENTRY_POINTS.PATH.ASSESSMENT_SKILL_SCORES().name,
            component: () => import('@views/tenant-app/AssessmentSkillScoresView.vue'),
            meta: getMeta({ title: 'Assessment | Scores' }),
          },
          {
            path: 'reviews/:reviewCode/manage',
            name: ENTRY_POINTS.PATH.REVIEW_MANAGE().name,
            component: () => import('@views/tenant-app/ReviewView.vue'),
            meta: getMeta({ title: 'Peer-Review', layout: 'secondary' }),
          },
          {
            path: 'reviews/:reviewCode/reviewees/:revieweeId',
            name: ENTRY_POINTS.PATH.REVIEW_REVIEWEE().name,
            component: () => import('@views/tenant-app/RevieweeView.vue'),
            meta: getMeta({ title: 'Peer-Review', layout: 'secondary' }),
          },
          {
            path: 'skills/:skillTreeCode/manage',
            name: ENTRY_POINTS.PATH.SKILL_TREE_MANAGE().name,
            component: () => import('@views/tenant-app/SkillTreeView.vue'),
            meta: getMeta({ title: 'Self-Review', layout: 'secondary' }),
          },
          {
            path: 'skills/:skillTreeCode/user',
            name: ENTRY_POINTS.PATH.TENANT_USER_SKILL_TREE().name,
            component: () => import('@views/tenant-app/TenantUserSkillTreeView.vue'),
            meta: getMeta({ title: 'Self-Review', layout: 'secondary' }),
          },
        ],
      },
      {
        path: 'teams',
        component: () => import('@views/tenant-app/TeamsRootView.vue'),
        meta: getMeta({}),
        children: [
          {
            path: 'dashboard',
            name: ENTRY_POINTS.PATH.TEAMS_HOME().name,
            component: () => import('@views/tenant-app/TeamsDashboardView.vue'),
            meta: getMeta({ title: 'Teams' }),
          },
          {
            path: ':teamCode',
            component: () => import('@views/tenant-app/TeamRootView.vue'),
            meta: getMeta({}),
            children: [
              {
                path: '',
                name: ENTRY_POINTS.PATH.TEAM_HOME().name,
                component: () => import('@views/tenant-app/TeamView.vue'),
                meta: getMeta({ title: 'Team' }),
              },
              {
                path: 'search',
                name: ENTRY_POINTS.PATH.TEAM_SEARCH_TENANT_USERS().name,
                component: () => import('@views/tenant-app/TeamSearchTenantUserView.vue'),
                meta: getMeta({ title: 'Team | Search' }),
              },
              {
                path: 'scout',
                name: ENTRY_POINTS.PATH.TEAM_FREE_SEARCH_TENANT_USERS().name,
                component: () => import('@views/tenant-app/TeamFreeSearchTenantUserView.vue'),
                meta: getMeta({ title: 'Team | Scout' }),
              },
            ],
          },
        ],
      },
      {
        path: 'users',
        component: () => import('@views/tenant-app/UserRootView.vue'),
        redirect: ENTRY_POINTS.PATH.USER_HOME(),
        meta: getMeta(),
        children: [
          {
            path: '',
            name: 'tenant-users',
            component: () => import('@views/tenant-app/UserListView.vue'),
            meta: getMeta({ title: 'Users' }),
          } as RouteRecordRaw,
          {
            path: ':tenantUserId',
            name: ENTRY_POINTS.PATH.TENANT_USER().name,
            component: () => import('@views/tenant-app/UserView.vue'),
            meta: getMeta({ title: 'User' }),
          } as RouteRecordRaw,
        ],
      } as RouteRecordRaw,
      ...tenantAdminRoutes,
    ],
  },
];

export default routes;
